// ─────────────────────────────────────────────
//  timeline — pure model for the Timeline Chart (Gantt) page.
//
//  Turns the live rooms + locations + assignments into a render-ready model:
//  occupied rooms only, grouped by facility section (location order), each room
//  carrying its case bars (start/end in minutes), plus a shared time axis sized
//  to the day's actual span so the chart fits the screen width with no scroll.
//
//  Pure — no React, no Firestore. Reuses the board's room↔assignment matching
//  (roomForOrRoom) and section grouping (groupRoomsByLocation) so the timeline
//  stays consistent with the OR Board.
// ─────────────────────────────────────────────
import { groupRoomsByLocation } from "./locations";
import { roomForOrRoom } from "./gridTemplate";
import { shiftCodeEndHour24, goHomeHourToClock } from "./schedule";
import { orientationOf, seatName } from "./orientation";
import { promotePreceptorOutPatch, swapPairPatch } from "./secondProviderWrite";
import {
  countsAsCover,
  isDeparted,
  mergeSecondProviders,
  roomSeatName,
  seatShiftCode,
  secondProviderOf,
  SECOND_PROVIDER_ROLES,
} from "./secondProvider";
import {
  doctorsAvailableAt,
  prepareDoctors,
  requiredDoctors,
  roomsRunningAt,
} from "./doctorCoverage";

// Section tints — mirror BoardPage's LOCATION_PALETTE so a section keeps the
// same colour family across the board and the timeline. { bg, border, head }.
export const TIMELINE_PALETTE = [
  { bg: "#dfe7ff", border: "#b6c6fb", head: "#3849b8" }, // indigo
  { bg: "#cdeede", border: "#9bdcbd", head: "#0c7a52" }, // emerald
  { bg: "#ffe6ad", border: "#f4d488", head: "#9a6712" }, // amber
  { bg: "#ffdfe6", border: "#f7bccb", head: "#be3a5f" }, // rose
  { bg: "#d4eefb", border: "#a6dbf2", head: "#0e6f8a" }, // sky
  { bg: "#e7defb", border: "#cfc0f4", head: "#6b46c1" }, // violet
  { bg: "#e4f3cf", border: "#cbe7a3", head: "#4d7c0f" }, // lime
  { bg: "#ffe6d2", border: "#fcc9a6", head: "#b5521f" }, // orange
];

export function sectionTint(index) {
  const n = TIMELINE_PALETTE.length;
  const i = Number.isInteger(index) ? ((index % n) + n) % n : 0;
  return TIMELINE_PALETTE[i];
}

// Default day window when there are no timed cases at all (07:00–17:00).
const DEFAULT_START_MIN = 7 * 60;
const DEFAULT_END_MIN = 17 * 60;
// Fallback case length when an assignment has a start but no/invalid end time.
// Exported because it is also the default length of a case CREATED from the
// timeline (click empty lane → start + DEFAULT_CASE_MIN): one constant, so the
// substituted length and the offered length cannot drift apart.
export const DEFAULT_CASE_MIN = 60;
// On-call (24h) CRNAs leave at 7 AM the next day → 31:00 = 1860 min. Renders as
// "7 AM" and counts as staying past every shift boundary (3/5/7/9 PM).
const ON_CALL_DEPART_MIN = 31 * 60;

// Shift-change boundaries for the staffing strip: 3 / 5 / 7 / 9 PM.
export const SHIFT_BOUNDARIES = [
  { min: 15 * 60, label: "3 PM" },
  { min: 17 * 60, label: "5 PM" },
  { min: 19 * 60, label: "7 PM" },
  { min: 21 * 60, label: "9 PM" },
];
// Rooms finishing within this many minutes BEFORE a boundary count toward the
// earlier shift (they still need coverage at the boundary).
export const STAFF_BUFFER_MIN = 15;

// A CRNA's departure (go-home) time in minutes, from an explicit depart time or
// the shift code, or null when unknown.
//
// ⚠️ THE CODE COMES FROM `seatShiftCode`, NOT STRAIGHT OFF THE ROW. On a board
// imported before the pair-aware parser shipped, a two-CRNA room has the whole
// sentence on `ahpName` and `ahpShiftCode: null` — so this returned null and
// the bar printed the seat holder's name with NO leaving time and NO hatch,
// beside single-provider rooms that showed theirs correctly. The `5` in
// "Trevor Stamand 5 orient Kayla Gibbons" is his, and `seatShiftCode` reads it
// off his own side of the cell when — and only when — no structured field
// carries one. Single-provider rows are untouched: `ahpShiftCode` is present
// on them, and it always wins.
export function crnaDepartMinutes(assignment) {
  if (!assignment) return null;
  const code = seatShiftCode(assignment);
  if (isOnCallShift(code)) return ON_CALL_DEPART_MIN;
  const explicit = hmToMinutes(assignment.ahpDepartTime);
  if (explicit != null) return explicit;
  const hour = shiftCodeEndHour24(code);
  return hour == null ? null : hour * 60;
}

// On-call 24h shift code: "24", or an am/pm range with identical ends ("7a-7a").
//
// ⚠️ EXPORTED, because the SHEET IMPORTER has to ask the same question. It used
// to decide on-call from the absence of a case time alone — see
// features/sheet-import/buildBoardWrites — which made every no-time row a
// standing watch. `domain/tvPanels` carried a hand-copied twin of this function
// for the same reason; it now imports this one.
export function isOnCallShift(code) {
  const c = String(code || "").trim().toLowerCase();
  if (c === "24") return true;
  const m = c.match(/^(\d{1,2}[ap])-(\d{1,2}[ap])$/);
  return Boolean(m && m[1] === m[2]);
}

// ── THE STANDING OB WATCH ────────────────────
//
// The on-call CRNA covers epidurals and C-sections around the clock. BETWEEN
// those cases they are not busy — they are free in OB (baby place) and free to
// be called. That is the service the product owner described, and the board has
// been drawing the opposite of it.
//
// A row that describes NO CASE says so in one of two ways:
//   · no times at all — a hand-made on-call row, and
//   · 07:00 → 07:00 — the sheet importer's full-day placeholder, written
//     whenever the CRNA cell said "24" and the sheet gave no case times
//     (features/sheet-import/buildBoardWrites.js:33-34, and the identical
//     default in DailyAssignmentPage.js:315-316).
//
// NEITHER MAY DRAW A BAR. A full-width band asserts the CRNA is occupied from
// 7 AM to 7 AM, which is the exact opposite of what the row means, and every
// consumer of `placed` then inherits that lie: OB counts as a running room, the
// CRNA counts as occupied at every boundary, and the double-booking check flags
// them against themselves the moment they legitimately take a second room.
//
// An on-call row carrying a REAL window (a section booked 10:00–11:30) is a
// case and draws normally. That is the whole distinction: a watch has no window,
// a case has one.
//
// ⚠️ A ROW IS NEVER REMOVED, ONLY A CHART ENTRY. `domain/coverage.js` reads the
// ASSIGNMENT row, not `placed`, so the OB row must keep existing in Firestore —
// it is what the "OB must always be covered" rule is evaluated against.
export function isCaselessWindow(row) {
  const s = hmToMinutes(row?.startTime);
  const e = hmToMinutes(row?.endTime);
  // Either end missing = no window we can honestly draw. Same reading
  // `onCallWindows.isFullDay` takes, and that helper now delegates here so the
  // chart and the on-call edit form cannot drift apart about what "all day"
  // means.
  if (s == null || e == null) return true;
  return s === e;
}

// The row IS a standing watch: flagged on call AND carrying no case window.
export function isStandingOnCall(a) {
  return Boolean(a && a.isOnCall) && isCaselessWindow(a);
}

// Does this assignment row still CLAIM the people standing on it — the CRNA on
// `ahpId`, the orientee beside them and the supervising MD on `attendingId`?
//
// ⚠️ THE RULE THIS ENCODES, OWNER 2026-08-24: "as soon as there are no more
// cases in the room, the providers and the supervisors are released to free
// now." Before this, ANY row naming a person marked them busy for the whole day,
// so a CRNA whose only room finished at 11:00 never appeared on FREE NOW again —
// the board showed nobody available while people stood in the hallway.
//
// ⚠️ ONLY THE END HALF OF THE `roomsRunning` TEST, ON PURPOSE. That test also
// asks `startMin <= T`, because a room is not RUNNING before its first case. A
// PERSON is not FREE before it either — they are due in that room at 13:00. The
// user said "no MORE cases", which is about the day's remaining work, not about
// this minute. So a future case still claims; only a finished one lets go.
//
// The 15-minute tail is `STAFF_BUFFER_MIN`, the SAME grace the running test
// gives a room. A case ending at 11:00 does not put its CRNA in the hallway at
// 11:00 — there is emergence and a transport to PACU. One constant, so the chart
// and the bench cannot disagree about when a room lets go of somebody.
//
// ⚠️ FAILS CLOSED, AND THAT DIRECTION IS THE WHOLE SAFETY ARGUMENT. Saying
// "free" about somebody who is actually in a room sends a charge nurse looking
// for a person who cannot come; saying "busy" about somebody who is free only
// leaves them where they already were. So every case we cannot prove has ended
// keeps claiming:
//   * no clock handed in  → claims (this is also the legacy behaviour)
//   * no parseable end    → claims (an open-ended case is still running)
//   * end <= start        → claims (an overnight row: 19:00→07:00 has NOT ended
//                           at 08:00, and comparing the raw minutes would say it
//                           had, releasing the one person still in the building)
// The ORIENTEES card's rows — every second provider standing on the board,
// deduped per person-and-room.
//
// ⚠️ PURE AND HERE, NOT INLINE IN THE PAGE. It lived as a `useMemo` inside
// TimelineChartPage, which has no test file, so the rule was invisible to the
// suite: 4444 tests could pass with it broken. It also has to run the SAME
// clock as the break board — an orientee is released by their room exactly when
// the seat holder is, and filtering one card but not the other showed a single
// finished room in two states at once.
export function orienteeBoardRows(assignments, nowMin = null) {
  // key → the row already built for that person-and-room, so a second claiming
  // case adds its id to that row rather than being dropped on the floor.
  const seen = new Map();
  const rows = [];
  for (const a of assignments || []) {
    if (!rowClaimsAt(a, nowMin)) continue;
    const sp = secondProviderOf(a);
    if (!sp || !sp.name) continue;
    const room = String(a.orRoom || "").trim();
    const dedupe = `${sp.key}::${room}`;
    // ⚠️ A SECOND CLAIMING CASE IS NOT A DUPLICATE TO DISCARD. `rowClaimsAt`
    // deliberately lets a FUTURE case claim ("only a finished one lets go"), so a
    // room running two cases today reaches here twice for the same pair. The card
    // still shows ONE row — two identical lines would be noise — but the row has
    // to carry EVERY assignment it stands for, or a control acting on it writes to
    // the first case and leaves the rest disagreeing: the correction silently
    // undoes itself when the next case starts.
    const already = seen.get(dedupe);
    if (already) {
      if (a.id) already.assignmentIds.push(a.id);
      continue;
    }
    // ⚠️ THE GATE IS SPELLED ONCE, IN THE WRITE. `promotePreceptorOutPatch`
    // decides whether the move-out can happen at all — this asks it rather than
    // re-deriving the same three conditions, so a card can never offer a button
    // the write would refuse. Only the WORDING of the refusal lives here.
    const canMoveOut = promotePreceptorOutPatch(a) !== null;
    // ⚠️ ASKED THE SAME WAY, AND IT IS A DIFFERENT QUESTION. A swap needs a
    // resolved second person and an occupied seat; it does NOT need the pair to
    // be an orientation, because two colleagues sharing a room can still be
    // recorded the wrong way round. So a HELPING pair that is offered no
    // move-out is still offered a swap.
    const canSwap = swapPairPatch(a) !== null;
    const built = {
      key: dedupe,
      id: sp.id || null,
      name: sp.name,
      room,
      // The person HOLDING the room. `seatName` and never the raw cell: a
      // legacy row's `ahpName` is still the whole "X orient Y" sentence, and
      // printing that as the preceptor's name is the bug this card reports on.
      preceptorName: seatName(a),
      // The row the control writes to. Without it the card would have to
      // re-find the assignment by name, which is how one trainee in two rooms
      // gets the wrong room moved.
      assignmentId: a.id || null,
      // Every claiming assignment for this person and room, `assignmentId` first.
      // A write that means "this pair is backwards" is true of the whole day in
      // that room, not only of the case that happens to be running now.
      assignmentIds: a.id ? [a.id] : [],
      canMoveOut,
      canSwap,
      // ⚠️ THE SWAP NEEDS ITS OWN SENTENCE. It is refused for a DIFFERENT reason
      // than the move-out — it does not care whether the pair is an orientation,
      // only whether the second person is a real staff record and somebody is in
      // the seat. Reusing `blockedReason` would explain a helping pair's missing
      // move-out on a control that was perfectly willing to run.
      swapBlockedReason: canSwap
        ? ""
        : !sp.id || String(sp.id).startsWith("sp:")
          ? "trainee is not matched to a staff record"
          : "nobody is holding the room",
      // ⚠️ A REFUSAL MUST BE VISIBLE. A disabled control with no reason reads as
      // broken, and the two reasons need different actions from a human: a
      // helping pair is correct as it stands, an unmatched trainee is a name
      // somebody has to fix in the import review before the room can change hands.
      blockedReason: canMoveOut
        ? ""
        : sp.role !== SECOND_PROVIDER_ROLES.ORIENTING
          ? "not an orientation"
          : "trainee is not matched to a staff record",
      // ⚠️ NO INVENTED HOUR. A trainee with no recorded departure gets an empty
      // label and the card says so in words; printing the seat holder's time
      // here would be a fabricated fact about a real person.
      departLabel: sp.departMin != null ? to12h(sp.departMin) : "",
      // ⚠️ AND AN ASSUMED HOUR SAYS SO. Since 39d1108 the importer BORROWS the
      // preceptor's shift when the sheet printed no number beside the trainee
      // (owner: *"no number assume is 3pm"*), which quietly reversed the rule
      // the line above states. The hour is good enough to schedule against and
      // not good enough to assert, so the card hedges it rather than printing an
      // assumption as a recorded fact.
      departAssumed: Boolean(a.orienteeShiftInherited) && sp.departMin != null,
    };
    seen.set(dedupe, built);
    rows.push(built);
  }
  // ⚠️ BY ROOM, AND THAT IS A CORRECTNESS PROPERTY. This sorted by `name` — the
  // SECOND PROVIDER's name — until 2026-09-10, when the owner pressed the new
  // ⇅ SWAP and the row he had just acted on jumped underneath the other one: a
  // swap changes who the second provider is, so the one control on the row
  // re-sorted the list beneath his cursor. Two presses then land on two
  // DIFFERENT rooms, the second of them somebody he never meant to touch. A row
  // must not move as a result of editing it.
  //
  // Room is also the question this card's own layout says comes first ("where
  // are they" before "when do they go"), and it is the one field on the row that
  // no control here can change.
  //
  // `numeric` because room numbers are numbers: a plain compare files WOR 10
  // between WOR 1 and WOR 2. Name breaks the tie when one room holds two rows.
  return rows.sort(
    (x, y) =>
      String(x.room || "").localeCompare(String(y.room || ""), undefined, { numeric: true }) ||
      x.name.localeCompare(y.name)
  );
}

export function rowClaimsAt(row, nowMin) {
  if (!row) return false;
  if (nowMin == null || !Number.isFinite(nowMin)) return true;
  const start = hmToMinutes(row.startTime);
  const end = hmToMinutes(row.endTime);
  if (end == null) return true;
  if (start != null && end <= start) return true;
  return end > nowMin - STAFF_BUFFER_MIN;
}

// Does this row put somebody IN a room — the question a display asks, which is
// not the question the bench asks.
//
// ⚠️ TWO QUESTIONS, AND THEY ANSWER DIFFERENTLY FOR THE ON-CALL CRNA ON PURPOSE.
// `benchProviders` asks "is this person free to be pulled" and answers YES for
// the OB watch: between sections they are free. This asks "which room are they
// standing in" and answers WC OB for the same person. Both are true, and naming
// them apart is what stops a third surface reading one as the other.
//
// The row that stations nobody is the caseless row that is NOT a watch. Live,
// 2026-09-13: Titilope Somade, a "9" with a `07:00 → 07:00` row in WOR 1 and no
// cases, read on the break board as "WOR 1 · in room since 07:00 · 7h33m" while
// the bench beside it correctly called her free. `rowClaimsAt` alone cannot see
// the difference — a caseless window claims all day by design, which is what
// keeps the OB watch on the board.
// ⚠️ THE ALL-DAY SENTINEL ONLY, NOT `isCaselessWindow`. That predicate also
// answers true when EITHER time is missing — and a row with no times is a real
// assignment the board simply cannot schedule (a grid-entered room, a legacy
// doc). Excluding those took people who ARE in a room off the break board,
// which is the one screen a colleague checks to find cover: the dangerous
// direction. So this asks the narrow question — did somebody write `07:00 →
// 07:00`, the synthesised window that means "this row has no case" — and an
// unreadable window keeps stationing its person, failing closed.
export function rowStationsAt(row, nowMin) {
  if (!rowClaimsAt(row, nowMin)) return false;
  if (row && row.isOnCall) return true;
  return !isAllDaySentinel(row);
}

// `07:00 → 07:00` — the window the importer synthesises for a row the sheet
// gave no case times. It means "there is no case here", and it is the ONE shape
// that must not be read as time spent in a room.
//
// ⚠️ NARROWER THAN `isCaselessWindow` ON PURPOSE: that one also answers true
// when either time is missing, and a row with no times is a real assignment the
// board simply cannot schedule. Only both times present AND equal is the
// sentinel.
export function isAllDaySentinel(row) {
  const s = hmToMinutes(row?.startTime);
  const e = hmToMinutes(row?.endTime);
  return s != null && e != null && s === e;
}

// Departure (minutes) for a ROSTER CRNA member { shiftCode, goHomeHour }. On-call
// → 7 AM next day; else the shift-code end hour, else the legacy goHomeHour.
//
// `departTime` ("HH:MM") is read FIRST and is what a member derived from an
// assignment row carries (domain/secondProvider.secondProviderMember): the
// sheet may print no shift code beside the second name while a human has typed
// an explicit leaving time on the row. Ordinary roster members never carry the
// field, so this branch is inert for them.
// Exported for `domain/bench`, which needs the SAME answer to decide whether a
// person on the bench is past their leaving time. A second implementation there
// would be a second definition of when a shift ends — and the on-call sentinel
// below (31:00, deliberately past midnight) is exactly the kind of detail a
// re-implementation gets wrong, sending the OB on-call CRNA home at 7:01 AM.
export function rosterCrnaDepartMin(c) {
  if (!c) return null;
  const stated = hmToMinutes(c.departTime);
  if (stated != null) return stated;
  if (isOnCallShift(c.shiftCode)) return ON_CALL_DEPART_MIN;
  if (c.shiftCode) {
    const h = shiftCodeEndHour24(c.shiftCode);
    if (h != null) return h * 60;
  }
  if (c.goHomeHour != null) {
    const { hour24 } = goHomeHourToClock(c.goHomeHour);
    if (hour24 != null) return hour24 * 60;
  }
  return null;
}

// Name key for matching a person named on an assignment row to a roster member.
// Roster names are "Josh Larson"; the importer writes assignment names as
// "Josh Larson, CRNA" — compare on the part before the role, honorific-blind.
// Same normalisation useBreakBoardRows and domain/myRooms use, for the same
// reason: two spellings of one person must not read as two people.
const rosterNameKey = (s) =>
  String(s || "").split(",")[0].trim().toLowerCase().replace(/^dr\.?\s+/, "");

// ── THE ORIENTEE'S OWN DEPARTURE ─────────────
//
// ⚠️ NOT THE PRECEPTOR'S. Two CRNAs in one room leave at two different times —
// the owner's whole point ("every one has different times leaving").
// `crnaDepartMinutes` resolves ONE departure, the seat's; reusing it here would
// print a confident wrong answer about when a room loses somebody.
//
// Best evidence available, in order, and NOTHING if there is none:
//   1. `orienteeDepartTime` — the field a human edit writes (mirrors ahpDepartTime);
//   2. `orienteeShiftCode` — the number the sheet prints beside the second name
//      ("Josh Larson 5 Orient Sam Gongora 3"); parsed, and until now discarded;
//   3. `orienting.shiftCode` — the token `secondProviderOf` recovered from
//      THEIR OWN side of a legacy cell. ⚠️ Without this step the resolver knew
//      "Titi Somade 3" and this function still answered null: the ONE resolver
//      had the fact and the departure path read past it, straight to a roster
//      the importer never put her on. Structured fields are asked first and
//      still win — a human edit outranks whatever the sheet once printed.
//   4. the orientee's entry on TODAY'S ROSTER, by id then by name. This is the
//      path that works for a board already in Firestore, and it is why the
//      orientee has to BE a roster member (see buildBoardWrites).
//
// null when the day records none: a bar then prints the name with no time,
// which is "we do not know" rather than "same as the person next to them".
export function orienteeDepartMinutes(assignment, orienting, rosterCrnas = []) {
  if (!assignment || !orienting) return null;
  const explicit = hmToMinutes(assignment.orienteeDepartTime);
  if (explicit != null) return explicit;
  const code =
    assignment.orienteeShiftCode == null || assignment.orienteeShiftCode === ""
      ? orienting.shiftCode
      : assignment.orienteeShiftCode;
  if (code != null && code !== "") {
    const asText = String(code);
    if (isOnCallShift(asText)) return ON_CALL_DEPART_MIN;
    const h = shiftCodeEndHour24(asText);
    if (h != null) return h * 60;
  }
  const roster = Array.isArray(rosterCrnas) ? rosterCrnas : [];
  const key = rosterNameKey(orienting.orienteeName);
  const member =
    (orienting.orienteeId && roster.find((m) => m && m.id === orienting.orienteeId)) ||
    (key && roster.find((m) => m && rosterNameKey(m.name) === key)) ||
    null;
  return member ? rosterCrnaDepartMin({ ...member, shiftCode: member.shiftCode == null ? null : String(member.shiftCode) }) : null;
}

// ── ONE DEFINITION OF "WHO HOLDS THIS ROOM" ──────────
//
// ⚠️ THREE FILES USED TO COMPUTE THIS INDEPENDENTLY, and all three had to
// agree byte for byte or a real conflict went unreported:
//   · `placed.crnaKey` here,
//   · `timelineCommit.keyOf` — whose comment says, in as many words, that it
//     must key "the way buildTimeline keys placed",
//   · `OutstandingFixModal.caseRowsFromLanes`, whose `rawKey` comment says it
//     is "byte-identical to what timeline.js puts on placed.crnaKey".
// Two comments promising to stay in sync with a third place is not a contract,
// it is a countdown. Changing the seat rule in one of them (which is exactly
// what happened when the bar label was fixed and these were not) makes the
// dragged case look like a DIFFERENT person to the detector, and the
// double-booking warning goes quiet on the one row that has two people in it.
//
// So there is one function now, and the other two call it.
//
// Accepts any of the three shapes these callers hold: an assignment row
// (`ahpName`, possibly the mashed orienting cell), a `buildTimeline` case
// (`provider` raw + `ahpName`), or a `placed` entry (`crnaName`, already the
// seat). `seatName` is what splits "Josh Larson 5 Orient Sam Gongora" down to
// the person actually holding the room.
export function crnaSeatName(row) {
  return roomSeatName(row);
}

// The key that identifies that person across the board. An id when we have one;
// otherwise the SEAT's name — never the raw cell, which names two people and
// therefore matches neither of them anywhere else.
export function crnaSeatKey(row) {
  if (!row) return null;
  return row.crnaKey || row.ahpId || crnaSeatName(row) || null;
}

// The second person on a case bar, or null. Shape is what `CaseBar` renders and
// what `caseBarSpeech` reads: { id, name, departMin, depart12h }.
function orienteeOf(assignment, rosterCrnas) {
  // ⚠️ THE ONE RESOLVER, not a second reading of the same fields. The chart, the
  // room modal, the quick-actions menu, the roster and the break board all have
  // to agree about who the second person in a room is, and the way they stop
  // agreeing is each one re-deriving it.
  const sp = secondProviderOf(assignment);
  if (!sp) return null;
  const departMin = orienteeDepartMinutes(
    assignment,
    // Their recovered code rides along: the resolver already read it off their
    // own side of the cell, and re-deriving it here would be the second reading
    // this comment block exists to forbid.
    { orienteeName: sp.name, orienteeId: sp.id, shiftCode: sp.shiftCode },
    rosterCrnas
  );
  return {
    id: sp.id,
    // Stable even when the importer resolved no id — a legacy row names both
    // people and identifies neither.
    key: sp.key,
    name: sp.name,
    // ⚠️ WHY THEY ARE THERE, PUBLISHED. Without it a surface that REPLACES the
    // second person cannot tell a trainee from a colleague helping out, and its
    // only safe move is to write no role at all — which means the relationship
    // can never be shown and quietly rots. `orienting` is NOT rendered anywhere
    // (owner, 2026-08-20 — the board shows two CRNAs, evenly) and it is NOT what
    // decides staffing cover; it identifies which storage slot a write targets.
    role: sp.role,
    orienting: sp.orienting,
    // The code the departure above was derived FROM, so a surface that displays
    // or rewrites it does not have to reach back to the row.
    shiftCode: sp.shiftCode,
    departMin,
    depart12h: departMin != null ? to12h(departMin) : "",
  };
}

// Staffing adequacy at each shift boundary.
//   placed      — flat cases { roomId, startMin, endMin, crnaKey, crnaName, departMin }
//   rosterCrnas — the day's CRNA/AA roster { id, name, shiftCode, goHomeHour }.
//                 Includes UNASSIGNED (bench) providers, who are available to
//                 cover but aren't in any room — so they count toward availability.
//   rooms    = distinct rooms still running at the boundary (with the −15m buffer)
//   crnaList = distinct CRNAs available past the boundary (assigned + unassigned),
//              each with depart12h, leavingSoon, an `unassigned` flag, and
//              `onCall`/`onCallRoom` for whoever holds the standing OB watch
//   crnas    = crnaList.length
//   delta    = crnas − rooms  (negative ⇒ short by |delta|)
//
// `onCallWatch` — the standing watches `buildTimeline` found (see
// isStandingOnCall). It is passed IN rather than re-derived here so the chart,
// the pool and the bench all answer "is this person the OB watch" from one
// place; two derivations of that fact would eventually disagree, and they would
// disagree about whether OB is covered.
export function buildStaffing(placed, rosterCrnas, onCallWatch = []) {
  const list = Array.isArray(placed) ? placed : [];
  const roster = Array.isArray(rosterCrnas) ? rosterCrnas : [];
  const placedKeys = new Set(list.map((p) => p.crnaKey).filter(Boolean));

  // ── TWO CRNAS IN A ROOM ARE TWO CRNAS ────────
  //
  // ⚠️ THIS BLOCK USED TO SAY THE OPPOSITE, and it said it twice: the second
  // person got no pool entry "and must not get one — they are present, not
  // cover", and the card hung their name off the preceptor as a subordinate
  // `↳` line. Both are now wrong, by product decision, and the wrong version is
  // quoted here so nobody reads the new code as an oversight and "fixes" it
  // back:
  //
  //   · 2026-08-20, the counting question put to the owner explicitly, with the
  //     2026-08-14 incident named — "BOTH CRNAS COUNT AS STAFFING COVER."
  //   · 2026-08-20, on seeing a card read `1 staying / Trevor Stamand 5 PM /
  //     ↳ Kayla Gibbons` — "i dont care who is orienting who, place the 2
  //     crnas." Two equals, listed the same way, counted the same way. The
  //     hierarchy stays in the DATA (the seat still holds the room, which is
  //     what `isSoloCase` reads) and leaves the DISPLAY.
  //
  // So the second person is a pool member in their own right. `countsAsCover`
  // gates them like anybody else, which is the one flag a future "students do
  // not count" rule flips.
  //
  // ⚠️ AND THEY MUST NOT LAND IN THE POOL TWICE. They arrive from two
  // directions — as a roster member (enrolled at import, or derived at read
  // time by mergeSecondProviders) and off the case row here — and a legacy pair
  // resolves no id for either person, so id-only matching would count one human
  // as two. Matched on the key `secondProviderOf` mints and on the name.
  // A key proves identity only when it is a real record id — not the person's
  // own name standing in for one, and not the synthetic `sp:` key minted for a
  // second provider nobody could resolve. Used by all four pool loops.
  const identifiedKey = (key, name) => Boolean(key) && key !== name && !String(key).startsWith("sp:");

  const secondPeople = new Map();
  for (const p of list) {
    if (!p) continue;
    const name = String(p.orienteeName || "").trim();
    if (!name) continue;
    const key = p.orienteeKey || p.orienteeId || `sp:${rosterNameKey(name)}`;
    const seen = secondPeople.get(key);
    if (!seen) {
      // ⚠️ NOT `Boolean(orienteeKey)`. `orienteeOf` hands back the SAME synthetic
      // `sp:` key when it could not resolve the person, so a truthy key proves
      // only that a key exists — not that anybody was identified.
      secondPeople.set(key, { key, name, departMin: p.orienteeDepartMin ?? null, identified: identifiedKey(key, name) });
    } else if (seen.departMin == null && p.orienteeDepartMin != null) {
      // Same person in two rooms today; take the departure we actually know.
      seen.departMin = p.orienteeDepartMin;
    }
  }

  // ── EVIDENCE THAT SOMEBODY IS HERE ───────────
  //
  // ⚠️ WHAT AN UNKNOWN DEPARTURE MEANS, DECIDED ON PURPOSE. The boundary filter
  // used to be `departMin != null && departMin > T`, so a person with no
  // recorded leaving time was dropped from EVERY boundary — they simply were
  // not in the count. That is the common case for a second provider, not an
  // edge case: a legacy "X orient Y" cell prints a shift number for the
  // preceptor and nothing for the second name, so the person the owner can see
  // standing in the room contributed zero.
  //
  // The rule now: a person with no recorded departure counts at a boundary for
  // exactly as long as there is POSITIVE EVIDENCE they are here — a case of
  // theirs that is still running at that boundary, judged by the SAME predicate
  // `roomsRunning` uses one screen below. Numerator and denominator then read
  // the same fact about the same room, which is the only version of this that
  // stays explainable to a charge nurse.
  //
  // ⚠️ THE TWO ANSWERS WE REFUSE TO GIVE:
  //   · borrowing the other CRNA's departure — that invents a leaving time for
  //     a real person, and the owner's whole point is that two people in one
  //     room leave at two different times;
  //   · "present all day" — that would tell the board it has cover at 9 PM from
  //     somebody who went home at 3, which is the dangerous direction. Evidence
  //     runs out when the case does, and so does the count.
  // Their `depart12h` stays empty throughout: we still do not know, and the
  // card must not print a time we made up.
  const presenceByKey = new Map();
  const addPresence = (key, p) => {
    if (!key) return;
    const arr = presenceByKey.get(key);
    if (arr) arr.push(p);
    else presenceByKey.set(key, [p]);
  };
  for (const p of list) {
    if (!p) continue;
    addPresence(p.crnaKey, p);
    const seatKey = rosterNameKey(p.crnaName);
    if (seatKey) addPresence(seatKey, p);
    const name = String(p.orienteeName || "").trim();
    if (!name) continue;
    addPresence(p.orienteeKey || p.orienteeId, p);
    addPresence(rosterNameKey(name), p);
  }
  const presenceFor = (entry) =>
    presenceByKey.get(entry.key) || presenceByKey.get(rosterNameKey(entry.name)) || [];
  // Identical to the `roomsRunning` test below, deliberately.
  const presentAt = (windows, T) =>
    windows.some((p) => p.startMin <= T && p.endMin > T - STAFF_BUFFER_MIN);

  // ⚠️ MATCHED BY ID *AND* BY NAME, because the two sides are keyed differently.
  // A pool entry is keyed `id || name` off the ROSTER, while the watch comes off
  // the ASSIGNMENT row — and a sheet cell the resolver could not match to a
  // staff record keeps its name and gets no `ahpId`. Looking up by id alone drops
  // that person's OB CALL flag silently, which is the "a missing id is not an
  // absent person" failure this codebase has already been bitten by once.
  const watchList = (Array.isArray(onCallWatch) ? onCallWatch : []).filter(Boolean);
  const watchByKey = new Map();
  for (const w of watchList) {
    if (w.ahpId) watchByKey.set(w.ahpId, w);
    const nm = String(w.ahpName || "").trim().toLowerCase();
    if (nm) watchByKey.set(nm, w);
  }
  const watchFor = (id, name) =>
    watchByKey.get(id) ||
    watchByKey.get(String(name || "").trim().toLowerCase()) ||
    null;

  // Availability pool: every rostered CRNA (assigned or bench), plus any placed
  // CRNA missing from the roster (defensive), plus the second person in every
  // two-CRNA room. Keyed so each person counts once.
  const pool = new Map();
  // …and indexed by NAME as well, because the three sources key differently: a
  // roster member by `id || name`, a case row by `ahpId || seat name`, a second
  // provider by the `sp:` key `secondProviderOf` mints. One human reaching the
  // pool under two of those is one human counted twice.
  const poolKeyByName = new Map();
  // People a product rule has explicitly taken OUT of the count. Tracked
  // separately because the second-provider pass below would otherwise put them
  // straight back in through the other door.
  const excludedNames = new Set();
  const excludedKeys = new Set();
  const addToPool = (key, entry) => {
    pool.set(key, entry);
    const nk = rosterNameKey(entry.name);
    if (nk && !poolKeyByName.has(nk)) poolKeyByName.set(nk, key);
  };
  // ⚠️ THE NAME INDEX HAS TO BE READ BY EVERY LOOP, NOT ONLY THE LAST TWO.
  // It used to be written by `addToPool` and consulted only by the second
  // provider and watch passes; the roster and case-row passes asked
  // `pool.has(key)` and nothing else. So one human reaching the pool under two
  // different keys was added twice — and that is the ORDINARY case, not an edge
  // one: a legacy cell like `"Josh Larson 5 orient Brianna Michaud"` resolves no
  // `ahpId`, so its seat keys by NAME while the same person's roster entry keys
  // by STAFF ID. 2026-08-20, Wesley Chapel: the card read `16 staying` with
  // `Josh Larson 5 PM` printed twice and `+11 spare` — one invented body in the
  // count, on the screen a charge nurse uses to decide whether the room is safe.
  //
  // ⚠️ AND WHY THE MATCH IS NOT SIMPLY BY NAME. Two different clinicians can
  // share a name, and silently folding them together would UNDERCOUNT staffing —
  // the dangerous direction, and unrecoverable because nothing on the card would
  // show it happened. So a name match only collapses when at least one side is
  // UNIDENTIFIED (keyed by its own name because no id was ever resolved). Two
  // entries that both carry real, different ids are two people and stay two,
  // whatever they are called.
  const inPool = (key, name, identified = identifiedKey(key, name)) => {
    if (key && pool.has(key)) return pool.get(key);
    const nk = rosterNameKey(name);
    const hit = nk ? poolKeyByName.get(nk) : null;
    const entry = hit ? pool.get(hit) : null;
    if (!entry) return null;
    return !entry.identified || !identified ? entry : null;
  };
  // One human arriving a second time under a different key: keep the entry
  // already in the pool, and take from the newcomer only what the first source
  // did not know. A departure we learn is a departure; a departure we do not
  // have must not overwrite one we do.
  const absorb = (entry, { departMin = null, unassigned = null, identified = false } = {}) => {
    if (entry.departMin == null && departMin != null) entry.departMin = departMin;
    if (unassigned === false) entry.unassigned = false;
    if (identified) entry.identified = true;
    return entry;
  };
  const isExcluded = (key, name) =>
    (key && excludedKeys.has(key)) || excludedNames.has(rosterNameKey(name));

  for (const c of roster) {
    const key = c && (c.id || c.name);
    if (!key || pool.has(key)) continue;
    // ⚠️ BOTH CRNAS IN A ROOM ARE COVER — product owner, 2026-08-20. The
    // question was put to him explicitly, naming the 2026-08-14 incident, and
    // he chose it: a second provider is a working anaesthetist, not a spectator.
    // This used to read `if (c.orienting) continue`, which excluded them.
    //
    // ⚠️ COUNTING NOW ASKS ONE FIELD AND NOTHING ELSE. `countsAsCover` is
    // absent on every member already in Firestore and absent means yes, so the
    // decision needed no migration; and a future "students do not count" rule
    // is `countsAsCover: false` at the one place that enrols them, not a new
    // branch here. `orienting` is left alone: it is a fact about the DATA (who
    // holds the seat, which is what `isSoloCase` reads), and it no longer
    // reaches this card at all.
    // ⚠️ AND SOMEBODY SENT HOME IS OUT OF THE COUNT TOO. `countsAsCover` asks
    // what KIND of person this is; `departed` asks whether they are still here.
    // Only the first was asked, so pressing Home moved a provider into the
    // bench's Home section and out of the break-relief candidates while the
    // adequacy strip went on counting them as cover — an overcount, which is
    // the dangerous direction on the screen that decides whether a room is
    // safe to keep running.
    //
    // ⚠️ IT MUST BE AN EXCLUSION, NOT A `continue`. The second-provider and
    // watch passes below re-admit anyone they find who is not in `pool`, so
    // skipping quietly here would let a departed CRNA back in through either
    // of the other two doors — which is exactly why the exclusion sets exist.
    if (!countsAsCover(c) || isDeparted(c)) {
      excludedKeys.add(key);
      const nk = rosterNameKey(c.name);
      if (nk) excludedNames.add(nk);
      continue;
    }
    // Already here under another key (an unresolved case row keys by name).
    // ⚠️ `c.id` IS NOT PROOF OF IDENTITY HERE. `mergeSecondProviders` derives a
    // roster member for the second person on a legacy cell and gives it the
    // synthetic `sp:` key as its id — so asking `Boolean(c.id)` would call that
    // derived member identified, and two entries that are both "identified"
    // never collapse. One definition, `identifiedKey`, for all four loops.
    const rosterIdentified = identifiedKey(key, c.name);
    const twin = inPool(key, c.name, rosterIdentified);
    if (twin) {
      absorb(twin, {
        departMin: rosterCrnaDepartMin(c),
        unassigned: placedKeys.has(key) ? false : null,
        identified: rosterIdentified,
      });
      continue;
    }
    const watch = watchFor(key, c.name);
    addToPool(key, {
      key,
      identified: rosterIdentified,
      name: c.name || "CRNA",
      departMin: rosterCrnaDepartMin(c),
      // A standing watch places nobody, so the OB CRNA reads `unassigned` here
      // for exactly as long as they are actually free — the moment they take a
      // room they gain a `placed` entry and this flips on its own.
      unassigned: !placedKeys.has(key),
      onCall: Boolean(watch),
      onCallRoom: watch?.roomName || "",
    });
  }
  for (const p of list) {
    if (p.crnaKey && !pool.has(p.crnaKey)) {
      if (isExcluded(p.crnaKey, p.crnaName)) continue;
      const identified = identifiedKey(p.crnaKey, p.crnaName);
      const twin = inPool(p.crnaKey, p.crnaName, identified);
      if (twin) {
        absorb(twin, { departMin: p.departMin, unassigned: false, identified });
        continue;
      }
      const watch = watchFor(p.crnaKey, p.crnaName);
      addToPool(p.crnaKey, {
        key: p.crnaKey,
        identified,
        name: p.crnaName || "CRNA",
        departMin: p.departMin,
        unassigned: false,
        onCall: Boolean(watch),
        onCallRoom: watch?.roomName || "",
      });
    }
  }
  // ⚠️ AND THE SECOND CRNA IN EVERY TWO-CRNA ROOM, as a member in their own
  // right. On a board built before enrolment existed they are on no roster doc,
  // so this loop is the ONLY thing that puts them in the count — which is
  // exactly what the owner was looking at when he said the extra CRNAs were
  // missing from staffing adequacy.
  //
  // They are in a case, so never `unassigned`. If the same human already
  // reached the pool from the roster we do not add a second entry — we only
  // fill in a departure the roster did not know, because the row may carry
  // their own shift code when the roster entry does not.
  for (const sp of secondPeople.values()) {
    if (isExcluded(sp.key, sp.name)) continue;
    const existing = inPool(sp.key, sp.name, sp.identified);
    if (existing) {
      absorb(existing, { departMin: sp.departMin, unassigned: false, identified: sp.identified });
      continue;
    }
    const watch = watchFor(sp.key, sp.name);
    addToPool(sp.key, {
      key: sp.key,
      identified: Boolean(sp.identified),
      name: sp.name,
      departMin: sp.departMin,
      unassigned: false,
      onCall: Boolean(watch),
      onCallRoom: watch?.roomName || "",
    });
  }
  // ⚠️ AND THE WATCH ITSELF, when the person is on neither list.
  // Before this change the standing watch produced a `placed` entry, so an
  // on-call CRNA who never made it onto the roster (an unresolved sheet name)
  // still reached the pool through the loop above. Dropping that entry would
  // have deleted them from the staffing maths entirely — the board would stop
  // counting the one person who is here all night. They are on call, so their
  // departure is the on-call departure, and they are free until placed.
  for (const w of watchList) {
    const key = w.ahpId || w.ahpName;
    if (!key || pool.has(key)) continue;
    if (isExcluded(key, w.ahpName)) continue;
    // The watch reaches the pool last, so it is the likeliest to arrive as the
    // second copy of somebody already counted.
    const twin = inPool(key, w.ahpName, Boolean(w.ahpId));
    if (twin) {
      absorb(twin, { departMin: ON_CALL_DEPART_MIN, identified: Boolean(w.ahpId) });
      twin.onCall = true;
      if (!twin.onCallRoom) twin.onCallRoom = w.roomName || "";
      continue;
    }
    addToPool(key, {
      key,
      identified: Boolean(w.ahpId),
      name: w.ahpName || "CRNA",
      departMin: ON_CALL_DEPART_MIN,
      unassigned: !placedKeys.has(key),
      onCall: true,
      onCallRoom: w.roomName || "",
    });
  }

  return SHIFT_BOUNDARIES.map(({ min: T, label }) => {
    const roomsRunning = new Set();
    for (const p of list) {
      // Room still running at T: started by T, ends after (T − buffer).
      if (p.startMin <= T && p.endMin > T - STAFF_BUFFER_MIN) roomsRunning.add(p.roomId);
    }
    const crnaList = [...pool.values()]
      // Staying past T: a recorded departure that is later than T, or — when
      // there is none — positive evidence they are still in a running case.
      // See the "EVIDENCE THAT SOMEBODY IS HERE" note above; the fallback is
      // the same test `roomsRunning` just applied, so the two sides of the
      // ratio never disagree about whether a room is going at T.
      .filter((c) =>
        c.departMin != null ? c.departMin > T : presentAt(presenceFor(c), T)
      )
      // Soonest to leave first. ⚠️ AN UNKNOWN DEPARTURE SORTS LAST, not first:
      // `a.departMin - b.departMin` reads null as 0, which put every person
      // whose leaving time we do not know at the head of a list whose whole
      // purpose is "who goes next".
      .sort((a, b) => {
        if (a.departMin == null) return b.departMin == null ? 0 : 1;
        if (b.departMin == null) return -1;
        return a.departMin - b.departMin;
      })
      .map((c) => ({
        key: c.key,
        name: c.name,
        departMin: c.departMin,
        // "" when we do not know — never a time we inferred from the colleague
        // standing next to them.
        depart12h: to12h(c.departMin),
        // ⚠️ NOT "LEAVING SOON" JUST BECAUSE WE DO NOT KNOW. `null <= x` is
        // TRUE in JavaScript (null coerces to 0), so without this guard every
        // person with no recorded departure would have lit up as imminently
        // leaving at every boundary — a false alarm on the one signal that is
        // supposed to mean "find cover now".
        leavingSoon: c.departMin != null && c.departMin <= T + STAFF_BUFFER_MIN,
        // We know they are here and we do not know until when. Published so a
        // card can say so instead of printing a blank where a time goes.
        departUnknown: c.departMin == null,
        unassigned: c.unassigned,
        onCall: c.onCall,
        onCallRoom: c.onCallRoom,
      }));
    const rooms = roomsRunning.size;
    const crnas = crnaList.length;
    const delta = crnas - rooms;
    const status = delta > 0 ? "surplus" : delta === 0 ? "tight" : "short";
    return { boundaryMin: T, label, timeLabel: minutesToHm(T), rooms, crnas, crnaList, delta, status };
  });
}

// ── Doctor staffing adequacy ─────────────────
// One supervising doctor covers up to 4 running rooms (facility ratio).
export const ROOMS_PER_DOCTOR = 4;

// A SOLO case: the supervising doctor is doing the case with NO CRNA, AA or
// resident in the room — either the explicit relief flag (assignment.soloMd)
// or an empty provider seat (how sheet-imported solo rows arrive; the room card
// renders both as "Solo — doctor runs the case").
//
// ⚠️ AN UNRESOLVED NAME IS A PERSON, NOT AN EMPTY SEAT. This used to read
// `attendingId && !ahpId` — a MISSING ID, which is a data-resolution artifact,
// not evidence that the doctor is alone. A sheet cell the importer could not
// match to a staff record keeps its NAME and gets no id, and every such room
// was declared solo. Live at Wesley Chapel on 2026-08-14: WOR 7's provider cell
// parsed as "Josh Larson 5 Orient Kayla Gibbons" (two names and a number
// mashed together), matched nobody, and so Jessica Huang — supervising WOR 7, 8
// and 12 — was pulled out of the supervising pool for the whole 3 PM boundary.
// The card read "Just enough" when the truth was one doctor spare, the room
// card claimed she was alone with the patient, and `mergeRoomsWithAssignments`
// BLANKED that room's CRNA, so messaging offered no chat with the person
// actually in the room.
//
// The seat is judged by the best evidence available, in order: an id, else a
// name, else a stand-in reliever. Only a seat with NONE of those — or the
// explicit clinical flag — is solo. A genuine sheet solo row still arrives with
// an empty CRNA cell (buildBoardWrites writes `ahpName: ""`), so it is
// unaffected.
export function isSoloCase(a, allRows = null) {
  if (!a) return false;
  if (a.soloMd) return true;
  if (!a.attendingId) return false;
  const seated = Boolean(
    a.ahpId ||
      String(a.ahpName || "").trim() ||
      a.crnaReliefId ||
      String(a.crnaReliefName || "").trim() ||
      // ⚠️ THE SECOND PROVIDER IS A PERSON IN THE ROOM, AND LEAVING THEM OUT WAS
      // THE OTHER HALF OF THE 2026-08-14 INCIDENT. That one is already pinned in
      // `isSoloCase.test.js`: a LEGACY pair written into `ahpName` read as an
      // empty seat, the doctor was pulled from the supervising pool and the room
      // card said she was alone with the patient. The guard closed the legacy
      // half only — a row carrying the pair in the STRUCTURED fields
      // (`ahpName: ""` with `orienteeName` set, which is what the importer
      // writes now, and what is left when the preceptor is taken out of a
      // teaching room) still read as solo.
      //
      // Owner saw the consequence on 2026-09-09: a bar reading *"Kinlap Mak
      // 3 PM + Cheryl Martin ·orientee"* — the SUPERVISING DOCTOR printed as the
      // person in the seat, because the label falls back to the attending on a
      // solo row, beside a trainee who was standing in that room the whole time.
      //
      // A trainee is not independent COVER — `orientation.js` is explicit and
      // nothing here changes the ratio — but they are unquestionably PRESENT,
      // and "solo" is the one question that asks exactly that: is the doctor
      // alone with the patient. With a trainee in the room the answer is no.
      secondProviderOf(a)
  );
  if (seated) return false;

  // ⚠️ AND A DOCTOR SUPERVISING ANOTHER ROOM IS NOT ALONE IN THIS ONE.
  //
  // An empty CRNA cell is what a genuine doctor-only room looks like AND what a
  // row whose CRNA is missing looks like — `buildBoardWrites` writes
  // `ahpName: ""` for both, so the seat cannot tell them apart. The day's other
  // rows can: a supervising anaesthetist holding rooms at the same hour cannot
  // simultaneously be the sole anaesthetist here.
  //
  // Live, 2026-09-13: WOR 12 ran a real case (surgeon Mumtaz, 07:30 → 11:40)
  // with no CRNA, and this answered solo — so the board printed **Jessica
  // Huang** as the person in the room while she was the attending on WOR 11,
  // WC CS and WC E3, three rooms that each had their own CRNA. Owner, sent the
  // board: "why there is to much diferences".
  //
  // ⚠️ THE COST IS NOT THE LABEL. A solo row pulls the doctor OUT of the
  // supervising pool — the 2026-08-14 incident named at the top of this
  // function, where the staffing card read "Just enough" while a doctor had
  // been removed from the count and a room card said she was alone with a
  // patient. A false solo asserts all of that about somebody running three
  // other rooms.
  //
  // ⚠️ EVIDENCE ONLY, NEVER AN ASSUMPTION. `allRows` is optional and every
  // existing caller passes one row; without the day's rows this answers exactly
  // as it always has. Absence of evidence must not flip a clinical reading.
  // And only a row with somebody actually IN it counts as supervision: two
  // seatless rooms under one doctor are two rooms with no CRNA, and reading one
  // as cover for the other would quietly un-solo both.
  if (Array.isArray(allRows) && a.attendingId) {
    const win = caseWindow(a);
    if (win) {
      for (const o of allRows) {
        if (!o || o === a || o.id === a.id) continue;
        if (o.attendingId !== a.attendingId) continue;
        if (!hasSeat(o)) continue;
        const ow = caseWindow(o);
        if (ow && ow.startMin < win.endMin && win.startMin < ow.endMin) return false;
      }
    }
  }
  return true;
}

// Is anybody actually standing in this room? The seat test `isSoloCase` uses,
// named so the supervision check above can ask it of a DIFFERENT row.
export function hasSeat(a) {
  return Boolean(
    a &&
      (a.ahpId ||
        String(a.ahpName || "").trim() ||
        a.crnaReliefId ||
        String(a.crnaReliefName || "").trim() ||
        secondProviderOf(a))
  );
}

// The minutes this row occupies, or null when it states no start. An unbounded
// row gets `DEFAULT_CASE_MIN` — the same assumption the chart draws it with, so
// a case nobody has bounded still counts as running rather than silently
// ceasing to overlap anything.
function caseWindow(a) {
  const startMin = hmToMinutes(a?.startTime);
  if (startMin == null) return null;
  let endMin = hmToMinutes(a?.endTime);
  if (endMin == null || endMin <= startMin) endMin = startMin + DEFAULT_CASE_MIN;
  return { startMin, endMin };
}

// Doctor staffing at each shift boundary (3/5/7/9 PM), mirroring the CRNA
// strip's room counting (same −15m buffer). Doctors normally carry NO shift
// departure and span every boundary — EXCEPT a locum with a locked leaving
// time (entry.leaveMin, minutes since midnight): they stop counting from the
// first boundary at/after that time, so the adequacy math excludes them from
// that shift onward.
//   rooms    = distinct rooms still running at the boundary
//   required = ceil(rooms / ROOMS_PER_DOCTOR)
//   doctors  = rostered doctors still available at the boundary (deduped)
//   delta    = doctors − required; status surplus/tight/short like the CRNA
//              strip. Zero rooms ⇒ nothing required ⇒ always "surplus".
// `ratio` is the FACILITY rule (facilityRules.supervisionRatio, clamped 1–4),
// defaulting to ROOMS_PER_DOCTOR so every existing caller is unchanged. It is a
// parameter and not a constant because a facility that supervises 1:3 must not
// read 1:4 here while the Doctors Needed box beside it reads 1:3 — two answers
// to one question on one screen.
export function buildDoctorStaffing(placed, rosterDoctors, ratio = ROOMS_PER_DOCTOR) {
  const list = Array.isArray(placed) ? placed : [];
  // Dedupe, leaving times and the running-room count are all shared with
  // `doctorSteps` via domain/doctorCoverage — the four fixed boundaries below
  // and that module's real crossings must never disagree about who is here.
  const allDoctors = prepareDoctors(rosterDoctors, { hmToMin: hmToMinutes });

  return SHIFT_BOUNDARIES.map(({ min: T, label }) => {
    const at = roomsRunningAt(list, T, STAFF_BUFFER_MIN);
    const roomsRunning = at.rooms;
    const soloRoomsAtT = at.soloRooms;
    // Split: Solo (running their own case at T) vs Supervision Available.
    // The split is per-boundary, so a doctor whose solo case ENDS before a
    // later boundary automatically returns to the supervision pool there.
    const { doctorList, soloList } = doctorsAvailableAt(allDoctors, T, at);
    // A solo attending who is NOT on this board's roster lists (e.g. Tampa
    // sheet cases whose MDs never joined supervisors[]) must still SHOW in
    // the Solo column — pull them straight from the running solo cases.
    const shownSolo = new Set(soloList.map((d) => String(d.name || "").toLowerCase()));
    for (const p of list) {
      if (!p.solo || !(p.startMin <= T && p.endMin > T - STAFF_BUFFER_MIN)) continue;
      const nm = String(p.attendingName || "").toLowerCase();
      const key = p.attendingId || p.attendingName;
      if (!key || (nm && shownSolo.has(nm)) || soloList.some((d) => d.key === key)) continue;
      shownSolo.add(nm || String(key));
      soloList.push({ key, name: p.attendingName || "Doctor", leaveMin: null, offRoster: true });
    }
    soloList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    const doctors = doctorList.length;
    const rooms = roomsRunning.size;
    // Solo rooms need no supervising MD — subtract them before dividing.
    const supervisionRooms = Math.max(0, rooms - soloRoomsAtT.size);
    const required = requiredDoctors(supervisionRooms, ratio);
    const delta = doctors - required;
    const status =
      required === 0 ? "surplus" : delta > 0 ? "surplus" : delta === 0 ? "tight" : "short";
    return {
      boundaryMin: T, label, timeLabel: minutesToHm(T),
      rooms, soloRooms: soloRoomsAtT.size, supervisionRooms,
      required, doctors, doctorList, soloList, delta, status,
    };
  });
}

// ── Locum leaving times ──────────────────────
// Locums on the MD call list (isLocum) may get a locked "leaving time" on the
// Timeline page (daily_boards.locumLeaveTimes: { CODE: "HH:MM" }). This
// helper stamps `leaveMin` onto the matching roster-doctor entries (matched
// by staff shortCode → id, falling back to honorific-insensitive name) so
// buildDoctorStaffing drops them from the boundaries at/after their leave.
const normName = (n) => String(n || "").replace(/^dr\.?\s+/i, "").trim().toLowerCase();
export function attachLocumLeaves(rosterDoctors, mdCallList, leaveTimes, staff = []) {
  const docs = Array.isArray(rosterDoctors) ? rosterDoctors : [];
  const calls = Array.isArray(mdCallList) ? mdCallList : [];
  const times = leaveTimes && typeof leaveTimes === "object" ? leaveTimes : {};
  const codes = Object.keys(times).filter((c) => hmToMinutes(times[c]) != null);
  if (!codes.length) return docs;
  const byId = new Map();
  const byName = new Map();
  for (const code of codes) {
    if (!calls.some((e) => e && e.code === code)) continue; // gone from call list
    const min = hmToMinutes(times[code]);
    const st = (staff || []).find((s) => String(s?.shortCode || "").toUpperCase() === code.toUpperCase());
    if (st) {
      byId.set(st.id, min);
      if (st.uid) byId.set(st.uid, min);
      byName.set(normName(st.name), min);
    }
    const entry = calls.find((e) => e && e.code === code);
    if (entry?.name) byName.set(normName(entry.name), min);
  }
  return docs.map((d) => {
    const min = byId.get(d.id) ?? byName.get(normName(d.name)) ?? null;
    return min == null ? d : { ...d, leaveMin: min };
  });
}

// "HH:MM" → minutes since midnight, or null when unparseable.
export function hmToMinutes(hm) {
  if (typeof hm !== "string") return null;
  const m = hm.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

// minutes → "HH:MM" (24h, zero-padded).
export function minutesToHm(min) {
  const v = Math.max(0, Math.round(min));
  const h = Math.floor(v / 60) % 24;
  const m = v % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// ── Shift-time colour coding ──────────────────
// CRNA names are colour-coded by their shift label everywhere the label rides
// along with a name (timeline bars, staffing-adequacy cards):
//   3 PM amber · 5 PM blue · 7 PM violet · 9 PM red · anything else gray.
// Text colour only — room/section block backgrounds keep their tints.
// ⚠️ THESE ARE 11-12px TEXT. They must clear 4.5:1 on white AND on all five
// Gantt bar tints, because that is where they are actually read.
//
// The 9 PM step was fixed first (see below) and the other three were never
// checked. Measured 2026-08-08: 3 PM #d97706 was **3.19:1** as an 11px bar
// label — failing; 5 PM #2563eb sat adjacent to `accent.primary`; 7 PM #7c3aed
// was one step from the `cancelled` pill ink. Darkened so no step is confusable
// with an alarm or a status token.
//
// ⚠️ `shiftLabelColor` HAS SIX OUTPUTS, NOT FOUR. An earlier version of this
// comment claimed "every step clears AA" — it only covered the four keys in this
// map. The two fallbacks below are also rendered as 11px labels and were both
// failing: on-call teal was #0d9488 (2.96:1) and the non-standard-shift gray was
// #6b7280 (3.74:1). Both darkened. A ramp is not audited until every branch of
// the function that returns it has been.
export const SHIFT_LABEL_COLORS = {
  15: "#92400e", // 3 PM — amber (was #d97706, 3.19:1 and failing at 11px)
  17: "#075985", // 5 PM — blue  (was #2563eb, too near accent.primary)
  19: "#5b21b6", // 7 PM — violet (was #7c3aed, too near the cancelled pill)
  // 9 PM was #dc2626 — byte-identical to `accent.error` (tokens.js). On this
  // very page that same red is the "Short N" staffing badge
  // (TimelineChartPage.js:542), so "this CRNA leaves at 9" and "we are
  // short-staffed" were rendering in the same colour on the same screen. The
  // spectrum was picked as a ramp (amber → blue → violet → …) without checking
  // the last step against the error token.
  //
  // Rose extends the ramp past violet, stays distinct from it, and leaves red
  // to mean one thing only. #be185d on white is 6.0:1 — passes AA for normal
  // text, which the 12px shift labels need.
  21: "#be185d", // 9 PM — rose (NOT red; red is reserved for alarm)
};
export const SHIFT_COLOR_ON_CALL = "#0f766e"; // teal — on call (7 AM / 24 hrs); was #0d9488, 2.96:1 at 11px
export const SHIFT_COLOR_FALLBACK = "#4b5563"; // gray — non-standard shifts; was #6b7280, 3.74:1 at 11px

// Accepts the display label ("5 PM", "5:30 PM", "7 AM", "24 Hours", "On call").
// Standard on-the-hour boundaries get their colour; the on-call shift (a 7 AM
// next-morning departure, or any "24 hrs"/"on call" wording) gets teal;
// everything else falls back to gray.
export function shiftLabelColor(label) {
  const raw = String(label || "");
  if (/\b(24\s*(hours|hrs|h)|on[\s-]*call)\b/i.test(raw)) return SHIFT_COLOR_ON_CALL;
  const m = /^\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*$/i.exec(raw);
  if (!m) return SHIFT_COLOR_FALLBACK;
  if (m[2] && m[2] !== "00") return SHIFT_COLOR_FALLBACK;
  let h = Number(m[1]) % 12;
  if (m[3].toUpperCase() === "PM") h += 12;
  if (h === 7) return SHIFT_COLOR_ON_CALL; // 7 AM = the on-call departure
  return SHIFT_LABEL_COLORS[h] || SHIFT_COLOR_FALLBACK;
}

// minutes → 12-hour clock ("15:00" → "3 PM", "16:50" → "4:50 PM", noon → "12 PM").
export function to12h(min) {
  if (min == null || !isFinite(min)) return "";
  const dayMin = (((Math.round(min) % 1440) + 1440) % 1440);
  const h = Math.floor(dayMin / 60);
  const m = dayMin % 60;
  const ampm = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

// Percent left/width of a case bar within the axis span (clamped to 0–100).
export function barMetrics(startMin, endMin, axisStart, span) {
  if (!(span > 0)) return { left: 0, width: 0 };
  const rawLeft = ((startMin - axisStart) / span) * 100;
  const left = Math.max(0, Math.min(100, rawLeft));
  const rawWidth = ((endMin - startMin) / span) * 100;
  const width = Math.max(0, Math.min(100 - left, rawWidth));
  return { left, width };
}

// Build the full timeline model.
//
// @param rooms        room docs (id, name, location, …)
// @param locations    resolved location list (order + labels)
// @param assignments  assignment docs (orRoom, startTime, endTime, names, …)
// @returns {
//   sections: [{ key, label, tint, rooms: [{ id, name, cases: [case] }] }],
//   axisStart, axisEnd, span, hourMarks: [{ min, label }],
//   occupiedCount, caseCount
// }
// A `case` = { id, startMin, endMin, startLabel, endLabel, provider,
//              attending, procedure, surgeon, label, sub }.
// `ratio` is the facility's supervision rule, threaded straight through to
// buildDoctorStaffing so the adequacy card, the rail box and the Setup page
// all divide by the same number. Defaulted, so every existing caller is
// unchanged.
export function buildTimeline(rooms, locations, assignments, rosterCrnas, rosterDoctors = [], ratio = ROOMS_PER_DOCTOR) {
  const roomList = Array.isArray(rooms) ? rooms : [];
  const asgList = Array.isArray(assignments) ? assignments : [];

  // ⚠️ THE SECOND PROVIDER MAY NOT BE ON THE ROSTER DOC AT ALL. Enrolment
  // happens at IMPORT time, so every board built before that shipped names the
  // second CRNA on an assignment row and on no roster — which is why Brian
  // McCloud, Sam Gongora and Kayla Gibbons were visible on this chart and
  // missing from the daily roster, the Breaks panel and the staffing count on
  // the same screen. Deriving the member here from the rows the chart is
  // already reading fixes those boards with no write to a live hospital board.
  //
  // Idempotent by id AND by name, so a board imported after the fix (where they
  // are real roster members) gains nothing and counts nobody twice.
  const rosterAll = mergeSecondProviders(rosterCrnas, asgList);

  // Bucket each assignment under its matched room (board matching rules).
  const casesByRoom = new Map();
  const placed = []; // flat list for the staffing computation
  let lo = Infinity;
  let hi = -Infinity;

  // TIMELESS rows that are NOT a standing on-call watch are held until the axis
  // is known, then drawn as a full-width bar so the room still appears. A row
  // with a provider and no times is a case somebody has not timed yet — hiding
  // it would hide a staffed room.
  const onCallPending = [];
  // The standing OB watches (isStandingOnCall): people who are ON CALL and in no
  // case. They draw NOTHING, and are published so the bench, the staffing pool
  // and the OB-uncovered alarm all read one answer.
  const onCallWatch = [];

  // ⚠️ A DOCTOR WHO HAS GONE HOME DOES NOT SUPERVISE A ROOM. `rosterDoctors`
  // arrives already marked — `useControlPanelData` wraps it in `withDeparted` —
  // and this function simply never asked, so every room a departed doctor held
  // went on printing "⚕ <their name>" on the bar. Owner, 2026-09-10: "why anna
  // lipska stay in the board whe she is already departed", with seven rooms
  // still naming her while the MD rail beside them showed her struck through.
  //
  // The CRNA half of this file has done it since 2026-09-07 (`isDeparted(c)`
  // below), added after the adequacy strip counted somebody sent home as cover.
  // Doctors were never given the same treatment on this path — the same
  // asymmetry that let a departed doctor read as releasable in Doctors Needed.
  //
  // ⚠️ BY ID AND BY NAME. A legacy row carries the doctor's name and no
  // `attendingId`, so matching on id alone would leave exactly those rooms
  // naming a doctor who left.
  const departedDoctorIds = new Set();
  const departedDoctorNames = new Set();
  for (const d of Array.isArray(rosterDoctors) ? rosterDoctors : []) {
    if (!d || !d.departed) continue;
    if (d.id != null) departedDoctorIds.add(String(d.id));
    const nk = rosterNameKey(d.name);
    if (nk) departedDoctorNames.add(nk);
  }
  const attendingHasGone = (row, name) => {
    if (!name) return false;
    if (row.attendingId != null && departedDoctorIds.has(String(row.attendingId))) return true;
    return departedDoctorNames.has(rosterNameKey(name));
  };

  for (const a of asgList) {
    if (!a) continue;
    const room = roomForOrRoom(a.orRoom, roomList);
    if (!room) continue;

    const provider = String(a.ahpName || "").trim();
    // ⚠️ THE DISPLAY STRING ONLY. `attendingId` and `attendingName` below are
    // left exactly as the row has them: the row is the day's record of who ran
    // the case, the room modal reads `attendingId` to decide whether a solo case
    // has a doctor to hand back to, and `withDeparted` MARKS rather than filters
    // precisely so the rail's struck-through DEPARTED line still renders. What
    // stops is the bar CLAIMING she is supervising the room right now.
    const attendingRaw = String(a.attendingName || "").trim();
    const attending = attendingHasGone(a, attendingRaw) ? "" : attendingRaw;
    const procedure = String(a.procedure || "").trim();
    // The surgeon plus whatever the sheet cell carried beyond the name —
    // "Ahmadian" + "+1", meaning a second surgeon is in the room. The import
    // splits them so the name can LINK to the configured surgeon, and they are
    // put back together here: the board reads exactly as it always did, and
    // dropping the "+1" would trade a clinical fact for a tidy link.
    const surgeonName = String(a.surgeon || "").trim();
    const surgeonNote = String(a.surgeonNote || "").trim();
    const surgeon = [surgeonName, surgeonNote].filter(Boolean).join(" ");
    // Bar sub-line: procedure · surgeon · supervising MD ("⚕ name" so the
    // supervisor reads distinctly from the surgeon at a glance).
    const sub = [procedure, surgeon, attending ? `⚕ ${attending}` : ""].filter(Boolean).join(" · ");

    // ⚠️ A STANDING WATCH DRAWS NOTHING. This used to render full-width — the
    // whole axis hatched, "7 AM–7 AM" — which told the board that the OB CRNA
    // was in a case all day when the truth is the reverse: with no case they are
    // free to cover an OB emergency, and free to be called. Removing the bar is
    // also what removes the `placed` entry, and that is deliberate: OB stops
    // counting as a running room and the CRNA stops counting as occupied,
    // because neither is true. See isStandingOnCall for the full rationale.
    if (isStandingOnCall(a)) {
      // A row with no ahpId and no name is an empty on-call SLOT — nobody to put
      // on the bench. It is still a real row (TimelineOnCallCard renders and
      // edits it); it simply names no person for the pool.
      if (a.ahpId || provider) {
        onCallWatch.push({
          assignmentId: a.id,
          roomId: room.id,
          roomName: room.name,
          orRoom: a.orRoom || "",
          ahpId: a.ahpId || null,
          ahpName: provider,
          crnaKey: a.ahpId || provider || null,
        });
      }
      continue;
    }

    // ⚠️ AN ALL-DAY SENTINEL THAT IS NOT A WATCH DRAWS NOTHING EITHER. The
    // branch above covers `isOnCall AND caseless`, which was the whole of this
    // shape only while the importer flagged every timeless row on call. The
    // moment that narrowed to real call codes (ffc5ccb), Titilope Somade's empty
    // `07:00 → 07:00` row in WOR 1 fell through to the timed path, where
    // `endMin <= startMin` becomes `startMin + DEFAULT_CASE_MIN` — a phantom
    // one-hour bar, and WOR 1 counted as a running room with a case it does not
    // have.
    //
    // ⚠️ AND NOT FULL-WIDTH EITHER, which is what the timeless branch below
    // would have done. `07:00 → 07:00` is the window the importer SYNTHESISES to
    // mean "this row has no case times"; rendering it across the axis states the
    // provider is in a case all day, the exact lie the watch branch above exists
    // to prevent. The room has no cases, so it is not on the board — and the
    // person is on the bench, where `benchProviders` already puts them.
    //
    // The timeless branch keeps its own behaviour: a row with NO times is
    // unreadable rather than empty, and it still renders so the room appears.
    if (isAllDaySentinel(a)) continue;

    // A timeless row that is NOT an on-call watch still renders full-width, so
    // the room appears. Defer until the axis span is known.
    const startMin = hmToMinutes(a.startTime);
    if (startMin == null) {
      if (provider) {
        const sp = secondProviderOf(a);
        onCallPending.push({
          roomId: room.id, roomName: room.name, id: a.id, provider, attending, procedure, surgeon, sub,
          seatLabel: roomSeatName(a) || provider,
          orientee: orienteeOf(a, rosterAll),
          secondProviderRole: sp?.role || null,
          orienteeShiftCode: sp?.shiftCode ?? null,
          ahpId: a.ahpId || null, ahpName: a.ahpName || "",
          attendingId: a.attendingId || null, attendingName: a.attendingName || "",
          startTime: a.startTime || "", endTime: a.endTime || "",
          crnaKey: a.ahpId || provider || null, crnaName: provider || null,
        });
      }
      continue;
    }
    let endMin = hmToMinutes(a.endTime);
    if (endMin == null || endMin <= startMin) endMin = startMin + DEFAULT_CASE_MIN;
    // ⚠️ A LEAVING TIME BELONGS TO SOMEBODY. `crnaDepartMinutes` reads the ROW's
    // `ahpShiftCode` / `ahpDepartTime`, which outlive the person they described:
    // a seat emptied without clearing the code kept printing "3 PM" beside a
    // name that was no longer there — on the owner's 2026-09-09 bar, beside the
    // supervising DOCTOR. No seat holder, no hour.
    const seatHeld = Boolean(roomSeatName(a) || String(a.ahpName || "").trim());
    const departMin = seatHeld ? crnaDepartMinutes(a) : null;
    // Resolved ONCE and used by the bar, the flat `placed` entry and the key.
    // Deriving "who holds this room" three times in one loop is how the label
    // and the staffing card came to disagree in the first place.
    const orienting = orientationOf(a);
    // Resolved once, and used both for the label's last resort and for `placed`.
    //
    // ⚠️ THE DAY'S ROWS ARE HANDED IN, and that is what stops the bar printing a
    // supervising doctor as the person in the room. WOR 12 on 2026-09-13 ran a
    // real case with no CRNA; asked about that row alone this answered solo, and
    // the label's last resort then named Jessica Huang — who was supervising
    // three other rooms at the same hour. See `isSoloCase`.
    const solo = isSoloCase(a, asgList);
    // Solo judged on this row ALONE, but not once the day's rows are read: the
    // doctor is holding another room at the same time. That difference is the
    // only positive evidence the board has that the attending is NOT in here,
    // and it is what the bar's last resort turns on.
    const attendingBusyElsewhere = !solo && isSoloCase(a);
    // ⚠️ `roomSeatName`, NOT `seatName`. `seatName` only knows the "X orient Y"
    // spelling; a cell reading "Yarsellis Frometa 5/Titi Somade 3" came through
    // it whole and became one bar labelled with two people's names, one placed
    // entry, one key nobody else in the app could match. The seat is ONE person
    // however the sheet spelled the pair.
    const seat = roomSeatName(a);
    // The second provider, resolved ONCE for this row. Used by the bar's
    // `orientee`, by the two fields a writer reads and by the `placed` entry.
    const c0 = secondProviderOf(a);
    // …and their DEPARTURE resolved once, because `placed` needs it too: the
    // staffing pool gives this person an entry of their own, and it must not
    // have to re-derive when they leave (two derivations of one clock time is
    // how the card and the bar came to print different answers before).
    const second = orienteeOf(a, rosterAll);

    const c = {
      id: a.id,
      // Raw ids + room so the timeline can act on the underlying assignment
      // (replace/relieve a person, send home/unassigned, edit case times).
      roomId: room.id,
      roomName: room.name,
      ahpId: a.ahpId || null,
      ahpName: a.ahpName || "",
      attendingId: a.attendingId || null,
      attendingName: a.attendingName || "",
      startTime: a.startTime || "",
      endTime: a.endTime || "",
      startMin,
      endMin,
      startLabel: minutesToHm(startMin),
      endLabel: minutesToHm(endMin),
      start12h: to12h(startMin),
      end12h: to12h(endMin),
      provider,
      attending,
      procedure,
      surgeon,
      departMin,
      depart12h: departMin != null ? to12h(departMin) : "",
      // An on-call row that reaches HERE carries real case times — a section, not
      // a watch — so it draws like any other case. The flag is kept because
      // downstream still needs to know the seat is the on-call one:
      // `laneCoverage` folds it into the ON CALL tag and the outstanding-fix
      // flow prefers the on-call row when filling an uncovered room.
      onCall: Boolean(a.isOnCall),
      // Bar label per spec: provider name + their departure ("Bobby Fry 3 PM").
      //
      // ⚠️ THE SEAT, NOT THE RAW CELL. `ahpName` on an orienting row still reads
      // "Trevor Stamand 5 orient Kayla Gibbons" — one sentence, printed as one
      // provider, and clipped to "Trevor Stamand 5 orient Kayla…" on any bar
      // narrower than the sentence. That is what the product owner photographed
      // on 2026-08-18. `seatName` returns the person HOLDING the room; the
      // second person gets their own line and their own departure below.
      // `provider` is deliberately left raw — `useCaseCommit` and the
      // outstanding-fix flow write it back to `ahpName`.
      // ⚠️ THE ATTENDING IS THE LAST RESORT ONLY WHEN NOBODY ELSE IS IN THE ROOM.
      // A doctor-only row is the doctor's room and has printed that way for
      // months — unchanged. But a row with a SECOND PROVIDER and an empty seat is
      // a room with a person in it, and falling through to the attending there
      // states that a CRNA is present who is not: the owner's 2026-09-09 bar read
      // *"Kinlap Mak 3 PM + Cheryl Martin ·orientee"* — the supervising doctor
      // printed as the seat, his name appearing TWICE on one bar (here, and as
      // "⚕ Kinlap Mak" on the line below), beside the trainee who was actually
      // standing there. *"Kinlap mak 3 pm???"*
      //
      // "No CRNA" rather than the trainee's name: an orientee does not hold the
      // room (`domain/orientation`), so promoting them into the seat would be the
      // same fabrication in the other direction. The room is uncovered, the chip
      // still names who is in it, and NEEDS YOU NOW already reports the gap.
      // ⚠️ POSITIVE EVIDENCE ONLY. The 2026-09-09 fix asked whether a SECOND
      // provider was standing there, so an empty seat with nobody beside it
      // still fell through to the attending — and on 2026-09-13 WOR 12 printed
      // **Jessica Huang**, who was the attending on three other rooms at that
      // hour, each with its own CRNA.
      //
      // `attendingBusyElsewhere` is solo-by-itself AND not-solo-once-the-day's
      // rows are read: the one shape that PROVES this doctor is in another room.
      // A doctor-only room whose attending we cannot even identify (no
      // `attendingId`, so no overlap is checkable) keeps printing their name, as
      // it has for months — absence of evidence must not flip a clinical
      // reading, and "No CRNA" there would be a guess wearing a fact's clothes.
      label:
        seat || provider || (second || attendingBusyElsewhere ? "No CRNA" : attending) || "Case",
      sub,
      // The person standing with the seat, or null. Non-null ONLY when the room
      // holds two providers; every other bar keeps exactly the shape it had.
      orientee: second,
      // ⚠️ AND THE TWO FACTS A *WRITER* NEEDS, at the top level where the row
      // shape puts them. A surface that replaces the second person reads the
      // case, not the assignment doc, so without these it must either omit the
      // relationship (and can then never show it) or guess a default — and
      // guessing "orienting" relabels a colleague as somebody's trainee in
      // front of the department. Null on a single-provider case, which is what
      // "there is nobody to describe" should look like.
      secondProviderRole: c0?.role || null,
      orienteeShiftCode: c0?.shiftCode ?? null,
    };
    if (!casesByRoom.has(room.id)) casesByRoom.set(room.id, []);
    casesByRoom.get(room.id).push(c);
    placed.push({
      roomId: room.id,
      startMin,
      endMin,
      // ⚠️ THE SEAT, NOT THE RAW CELL — AND THE SAME MISTAKE ONE LAYER DOWN.
      // The bar label was fixed to `seatName` and this was left reading the raw
      // `provider`, so the Staffing adequacy card went on printing
      // "Trevor Stamand 5 orient Kayla Gibbons  5 PM" as one provider. Every
      // consumer of `placed` inherits this: the availability pool, the adequacy
      // counts, and the double-booking detector.
      //
      // ⚠️ AND THE KEY MATTERS MORE THAN THE NAME. `crnaKey` falls back to the
      // name when there is no `ahpId`, and a legacy orienting row has no id — so
      // the key WAS the whole sentence. The same person keyed from the roster
      // ("Josh Larson") and keyed from that assignment could never match: they
      // counted twice in the pool, and a preceptor genuinely standing in two
      // rooms was invisible to `doubleBookedProviders`. That is the "a missing
      // id is not an absent person" trap this file already carries a warning
      // about, hiding one level below where the warning is written.
      crnaKey: crnaSeatKey(a),
      crnaName: seat || null,
      departMin,
      // ── THE SECOND PERSON IN THE ROOM ──────────
      //
      // ⚠️ STILL A FIELD AND NOT A SECOND `placed` ENTRY, but for a reason that
      // has NOTHING to do with counting any more. `placed` is one row per CASE:
      // `roomsRunning` counts distinct `roomId`s from it and `doubleBooking`
      // asks who is in two rooms at once. A second row for the same case would
      // not change the room count, but it WOULD make every pair look like a
      // double-booking of a room with itself. So the case stays one row and the
      // second person rides on it — and `buildStaffing` gives them a pool entry
      // of their own from these fields.
      //
      // `orienteeKey` is what makes that entry line up with the roster: a
      // legacy pair resolves no id, and keying on a raw name would put the same
      // human in the pool twice. `orienteeDepartMin` is THEIR OWN departure —
      // never the seat holder's, which would fabricate a leaving time for a
      // real person.
      orienteeId: orienting?.orienteeId || null,
      orienteeName: orienting?.orienteeName || "",
      orienteeKey: second?.key || null,
      orienteeDepartMin: second?.departMin ?? null,
      // Solo case (soloMd flag OR attending with no CRNA). Feeds the doctor
      // staffing split — a solo doctor can't supervise while the case runs.
      attendingId: a.attendingId || null,
      attendingName: a.attendingName || "",
      solo,
    });
    if (startMin < lo) lo = startMin;
    if (endMin > hi) hi = endMin;
  }

  // Axis: snap to whole hours around the day's actual span; default when empty.
  let axisStart;
  let axisEnd;
  if (!isFinite(lo)) {
    axisStart = DEFAULT_START_MIN;
    axisEnd = DEFAULT_END_MIN;
  } else {
    axisStart = Math.floor(lo / 60) * 60;
    axisEnd = Math.ceil(hi / 60) * 60;
    if (axisEnd - axisStart < 60) axisEnd = axisStart + 60;
  }
  const span = axisEnd - axisStart;

  // On-call CRNAs span the whole visible day; departure = 7 AM next day so they
  // read as "7 AM" and count as staying past every shift boundary.
  for (const oc of onCallPending) {
    const c = {
      id: oc.id,
      roomId: oc.roomId,
      roomName: oc.roomName,
      ahpId: oc.ahpId,
      ahpName: oc.ahpName,
      attendingId: oc.attendingId,
      attendingName: oc.attendingName,
      startTime: oc.startTime,
      endTime: oc.endTime,
      startMin: axisStart,
      endMin: axisEnd,
      startLabel: minutesToHm(axisStart),
      endLabel: minutesToHm(axisEnd),
      start12h: to12h(axisStart),
      end12h: to12h(axisEnd),
      provider: oc.provider,
      attending: oc.attending,
      procedure: oc.procedure,
      surgeon: oc.surgeon,
      departMin: ON_CALL_DEPART_MIN,
      depart12h: to12h(ON_CALL_DEPART_MIN),
      onCall: true,
      // Same seat rule as a timed case: an orienting pair on a timeless row is
      // still two people, and the raw cell is still one sentence.
      label: oc.seatLabel || oc.provider || oc.attending || "On call",
      orientee: oc.orientee,
      // Carried through the timeless path too, for the same reason: a writer
      // reads the CASE, and a timeless row can hold two people just as a timed
      // one can. Dropping them here would make the room modal behave
      // differently on an on-call row than on every other row, silently.
      secondProviderRole: oc.secondProviderRole,
      orienteeShiftCode: oc.orienteeShiftCode,
      sub: oc.sub ? `On call · ${oc.sub}` : "On call",
    };
    if (!casesByRoom.has(oc.roomId)) casesByRoom.set(oc.roomId, []);
    casesByRoom.get(oc.roomId).push(c);
    placed.push({
      roomId: oc.roomId,
      startMin: axisStart,
      endMin: axisEnd,
      crnaKey: oc.crnaKey,
      crnaName: oc.crnaName,
      departMin: ON_CALL_DEPART_MIN,
    });
  }

  // Occupied rooms only, grouped by section in board order.
  const occupied = roomList.filter((r) => casesByRoom.has(r.id));
  const grouped = groupRoomsByLocation(occupied, Array.isArray(locations) ? locations : []);

  const hourMarks = [];
  for (let m = axisStart; m <= axisEnd; m += 60) {
    hourMarks.push({ min: m, label: minutesToHm(m) });
  }

  const sections = grouped.map((sec, i) => ({
    key: sec.key,
    label: sec.label,
    tint: sectionTint(i),
    rooms: sec.rooms.map((r) => ({
      id: r.id,
      name: r.name,
      cases: (casesByRoom.get(r.id) || []).slice().sort((x, y) => x.startMin - y.startMin),
    })),
  }));

  let caseCount = 0;
  for (const arr of casesByRoom.values()) caseCount += arr.length;

  const staffing = buildStaffing(placed, rosterAll, onCallWatch);
  const doctorStaffing = buildDoctorStaffing(placed, rosterDoctors, ratio);

  // `placed` is exposed because it is the ONLY shape that carries a case's
  // provider and its minutes together — which is what the one-provider-two-rooms
  // check (domain/doubleBooking) needs. `sections` has already been folded into
  // per-room lanes by then and cannot answer "who, and when".
  // `onCallWatch` is exposed for the same reason `placed` is: it is the only
  // shape that carries WHO holds the standing watch and WHICH room it is for,
  // and three surfaces need exactly that — the bench chip, the FREE NOW chip and
  // the OB-uncovered alarm's wording. They read it; none of them re-derives it.
  return { sections, axisStart, axisEnd, span, hourMarks, occupiedCount: occupied.length, caseCount, staffing, doctorStaffing, placed, onCallWatch };
}

// ── Shift-end hatching on a case bar ─────────
// How much of a case bar to hatch, as a PERCENTAGE OF THAT BAR, marking the
// stretch the CRNA is still scheduled to be there. The chart already prints
// their departure as text ("Bobby Fry 3 PM"); the hatch makes the same fact
// scannable across a whole board without reading every bar.
//
// Percent of the BAR, not of the axis, because the overlay is a child of the
// bar — a bar that starts at 07:00 and ends at 17:00 with a 15:00 departure is
// hatched across its first 80%.
//
// Returns 0 when there is nothing honest to draw: no departure recorded, a
// zero-length bar, or a departure at/before the case starts. Returns 100 when
// they are there for the whole case, which is the common day-shift answer.
//
// Only CRNA bars hatch. A solo case's bar is labelled with the ATTENDING, and
// the client's rule is that surgeon and supervisor names carry no hatching —
// so a bar with no provider gets none, whatever departure it inherited.
export function shiftHatchPercent(c) {
  if (!c || !c.provider) return 0;
  const { startMin, endMin, departMin } = c;
  if (departMin == null || startMin == null || endMin == null) return 0;
  if (!(endMin > startMin)) return 0;
  if (departMin <= startMin) return 0;
  if (departMin >= endMin) return 100;
  return ((departMin - startMin) / (endMin - startMin)) * 100;
}
